import React, { Fragment, useCallback } from 'react';
import LearnositySharedPassage from './LearnositySharedPassage';
import LearnosityQuestionEditor from './LearnosityQuestionEditor';
import {
  MCQ_WIDGET_JSON,
  MCQ_WIDGET_TEMPLATE,
} from '../../learnosity/utils/constants';
import RenderPortalValidation from './EditItem/PortalValidation';
import TagsEditor from './TagsEditor';
import LearnosityMcqTemplate from './LearnosityMcqTemplate';
import { validationActionTypes } from './constants';

const McqEditor = ({
  isEdit,
  isLoading,
  subjectInfo = {},
  itemId,
  setPassageApiInstance,
  createdPassage,
  onAuthoringApiInstanceReady,
  authoringApiInstance,
  initOptions,
  dispatchValidation,
  showValidation,
  validations = {},
  tags,
  updateTags,
  flattenHierarchy,
  twoLevelQBFilterFF,
}) => {
  const hasLegacyDistractors = useCallback(() => {
    if (authoringApiInstance) {
      const questionsApp = authoringApiInstance.questionsApp();
      if (questionsApp) {
        const meta = questionsApp.getMetadata();
        return Object.values(meta).some(
          (data) => !!data.distractor_rationale_response_level,
        );
      }
    }
  }, [authoringApiInstance]);

  const updateMCQOptionClassNames = () => {
    const options = document.querySelectorAll('.lrn-mcq-option');

    options.forEach((option) => {
      if (option.classList.contains('lrn_selected')) {
        option.classList.add('--correct', '--is-selected');
      } else {
        option.classList.remove('--correct', '--is-selected');
      }
    });
  };

  const simulateClickOnOptionInputs = () => {
    // Walkaround to fix FY-36553 where LearnosityQuestionEditor sometimes fails to getWidget()
    // that causes the 'Preview Question' MCQ content not displaying the options.
    const option = document.querySelectorAll('.lrn-mcq-option.lrn_selected')[0];
    if (option) {
      const inputElement = option.querySelector('input');
      if (inputElement) {
        // Click/Unclick triggers the LRN event
        inputElement.click();
        inputElement.click();
      }
    }
  };

  return (
    <Fragment>
      <LearnosityMcqTemplate showCustomMeta={!hasLegacyDistractors()} />
      <RenderPortalValidation
        condition={showValidation && !validations.hasStimulus}
        elementClassName="error-stimulus-portal"
        message="In order to publish a question, you must enter the question text."
      />
      <RenderPortalValidation
        condition={showValidation && !validations.hasAllOptionsFilled}
        elementClassName="error-options-portal"
        message="In order to publish a question, you must enter content in each answer field."
      />
      <RenderPortalValidation
        condition={showValidation && !validations.hasOneCorrectOptionSelected}
        elementClassName="error-correct-portal"
        message="In order to publish a question, you must mark one answer correct."
      />
      <LearnositySharedPassage
        isDeletable
        isEdit={isEdit}
        isLoading={isLoading}
        isVisibleByDefault={Boolean(createdPassage)}
        onAddPassage={setPassageApiInstance}
        initOptions={initOptions}
      />
      <LearnosityQuestionEditor
        shouldSetWidgets={!itemId}
        widgetJson={MCQ_WIDGET_JSON}
        widgetTemplate={MCQ_WIDGET_TEMPLATE}
        initOptions={initOptions}
        isLoading={isLoading}
        onStimilusChange={(value) =>
          dispatchValidation({
            type: validationActionTypes.hasStimulus,
            payload: value,
          })
        }
        onOptionsChange={(options) =>
          dispatchValidation({
            type: validationActionTypes.hasAllOptionsFilled,
            payload: options,
          })
        }
        onValidationChange={(correctOptions) => {
          simulateClickOnOptionInputs();
          updateMCQOptionClassNames();
          dispatchValidation({
            type: validationActionTypes.hasOneCorrectOptionSelected,
            payload: correctOptions,
          });
        }}
        onReady={onAuthoringApiInstanceReady}
      />
      <TagsEditor
        isEdit={isEdit}
        subjectInfo={subjectInfo}
        tags={tags}
        updateTags={updateTags}
        showValidation={showValidation}
        validations={validations}
        questionType="MCQ"
        flattenHierarchy={flattenHierarchy}
        twoLevelQBFilterFF={twoLevelQBFilterFF}
      />
    </Fragment>
  );
};

export default McqEditor;
